import { Map } from '@app/services/export/ppt/classes/Map';
import PowerPointSlideProcessor from '@app/services/export/ppt/slides/PowerPointSlideProcessor';
import { pxToIn, richTextToPptxText } from '@app/services/export/ppt/utils/formattingUtils';
import { colors, presentationDimensions } from '@app/services/export/ppt/variables/constants';
import { copyMini, copySmall, h3 } from '@app/services/export/ppt/variables/typography';
import ITrainRouteSlideData from '@app/types/data/slides/ITrainRouteSlideData';
import PptxGenJS from 'pptxgenjs';

export default class PowerPointTrainRouteSlideProcessor extends PowerPointSlideProcessor<ITrainRouteSlideData> {
	protected masterName = this.slideData.id;

	public async processSlideData(): Promise<void> {
		this.presentation.defineSlideMaster({
			title: this.masterName,
			objects: Map.createMapMasterObjectsForImage(
				this.slideData.image,
				presentationDimensions.w,
				presentationDimensions.h
			)
		});

		this.createOverviewSlide();
		this.createTrainSlides();
	}

	protected createOverviewSlide(): void {
		const slide: PptxGenJS.Slide = this.presentation.addSlide({ masterName: this.masterName });

		this.createMapForSlide(slide, 'default');
	}

	protected createTrainSlides(): void {
		this.slideData.trains.forEach((train): void => {
			const slide: PptxGenJS.Slide = this.presentation.addSlide({ masterName: this.masterName });

			if (train.svgId) {
				this.createMapForSlide(slide, train.svgId);
			}

			if (train.image && train.image.width && train.image.height) {
				const trainImageWidth = 1;
				const trainImageHeight = 1;
				const trainImageX = presentationDimensions.w / 2 - trainImageWidth / 2;
				const trainImageY = presentationDimensions.h / 2 - trainImageHeight - 0.5;
				const trainTextY = trainImageY + trainImageHeight;
				const trainTextHeight = 0.8;
				const additionalInfoHeight = 2;

				// radial gradient behind train image
				slide.addImage({
					path: 'assets/images/ppt-transparent-radial-gradient.png',
					x: 1.5,
					y: trainImageY - 0.38,
					w: 7,
					h: 3.3
				});

				// train image
				slide.addImage({
					path: train.image.src,
					x: trainImageX,
					y: trainImageY,
					w: pxToIn(train.image.width),
					h: pxToIn(train.image.height),
					sizing: {
						type: 'contain',
						w: trainImageWidth,
						h: trainImageHeight
					}
				});

				const operatingTime = this.mapOperatingTimes(train.operatingTimes);

				// train text
				slide.addText(
					[
						{ text: train.name, options: { ...h3, breakLine: !!operatingTime } },
						{ text: operatingTime, options: { ...copySmall, paraSpaceBefore: 0.5 } }
					],
					{
						x: '15%',
						y: trainTextY,
						w: '70%',
						h: trainTextHeight,
						valign: 'top',
						align: 'center',
						color: colors.white
					}
				);

				const maintenanceInformation = this.mapMaintenanceTimes(train.operatingTimes);

				if (maintenanceInformation) {
					slide.addText(richTextToPptxText(`${this.i18n.maintenancePrefix} ${maintenanceInformation}`), {
						...copyMini,
						x: 0.4,
						y: presentationDimensions.h - additionalInfoHeight - 0.4,
						w: '35%',
						h: additionalInfoHeight,
						valign: 'bottom',
						align: 'left',
						color: colors.white
					});
				}
			}
		});
	}

	protected createMapForSlide(slide: PptxGenJS.Slide, svgId: string): void {
		if (this.slideData.image && this.slideData.mapLabels) {
			const map = new Map(
				slide,
				{
					image: this.slideData.image,
					map: this.slideData.map,
					labels: this.slideData.mapLabels,
					showCover: true,
					visibleSvgGroup: svgId,
					ignoreImage: true
				},
				{ x: 0, y: 0, w: presentationDimensions.w, h: presentationDimensions.h }
			);

			map.create();
		}
	}

	protected getSlideCount(): number {
		return this.slideData.trains.length + 1;
	}
}
